import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { subscriptionService } from "../../../services/subscriptionService";
import { couponService } from "../../../services/couponService";
import type { SubscriptionPlan } from "../../../types/payment";
import type { Coupon } from "../../../services/couponService";
import PaymentButton from "../../../components/payment/PaymentButton";
import { useTelemetry } from "../../../hooks/useTelemetry";
import { MODULES } from "../../../telemetry/constants/modules";
import { ACTIONS } from "../../../telemetry/constants/actions";
import logo from "../../../assets/new-logo.png";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // const { trackAction } = useTelemetry({ module: MODULES.BILLING, submodule: 'Subscription Checkout' });

  const userId = localStorage.getItem("user_id") || "";
  
  // State from location or API
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(location.state?.plan || null);
  
  const [planTypes, setPlanTypes] = useState<any[]>([]);
  const [selectedPlanType, setSelectedPlanType] = useState<any | null>(null);
  
  // Coupon state
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [couponError, setCouponError] = useState("");
  
  const [loadingPlans, setLoadingPlans] = useState(!location.state?.plan);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingCoupons(true);
        const [plansData, couponsData, planTypesData] = await Promise.all([
          subscriptionService.getPlans(userId || undefined),
          couponService.getAvailableCoupons(userId || undefined),
          subscriptionService.getPlanTypes()
        ]);
        
        setPlans(plansData);
        setAvailableCoupons(couponsData);
        setPlanTypes(planTypesData);
        
        if (planTypesData.length > 0) {
          setSelectedPlanType(planTypesData[0]);
        }
        
        if (!selectedPlan && plansData.length > 0) {
          setSelectedPlan(plansData[0]);
        }
      } catch (error) {
        console.error("Failed to load checkout data:", error);
      } finally {
        setLoadingPlans(false);
        setLoadingCoupons(false);
      }
    };
    
    loadData();
  }, []);

  // Reset coupon when plan changes
  useEffect(() => {
    if (selectedPlan && selectedPlanType) {
      setCouponCode("");
      setCouponApplied(false);
      setDiscountAmount(0);
      setCouponError("");
      
      couponService.preview(selectedPlan.id, null, selectedPlanType.id)
        .then(data => {
          setBasePrice(data.original_price);
          setFinalPrice(data.final_price);
        })
        .catch(() => {});
    }
  }, [selectedPlan, selectedPlanType]);

  const applyCouponCode = async (code: string) => {
    if (!selectedPlan) return;
    
    try {
      setApplyingCoupon(true);
      const result = await couponService.preview(selectedPlan.id, code, selectedPlanType?.id);
      
      setCouponCode(code);
      setDiscountAmount(result.discount_amount);
      setBasePrice(result.original_price);
      setFinalPrice(result.final_price);
      setCouponApplied(result.coupon_applied);
      setCouponError("");

      if (result.coupon_applied) {
        // trackAction(ACTIONS.APPLY_COUPON, `Apply coupon ${code}`, {
        //   coupon_code: code,
        //   plan_id: selectedPlan.id,
        //   discount_amount: result.discount_amount,
        //   final_price: result.final_price,
        // });
      }
    } catch (err) {
      setCouponError("Invalid or expired promo code.");
      setCouponApplied(false);
      setDiscountAmount(0);
      setFinalPrice(basePrice);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleSkipCoupon = () => {
    if (selectedPlan) {
      // trackAction(ACTIONS.REMOVE_COUPON, 'Skip/Remove coupon', {
      //   plan_id: selectedPlan.id,
      //   coupon_code: couponCode || undefined,
      // });
      setCouponApplied(false);
      setCouponCode("");
      setDiscountAmount(0);
      setFinalPrice(basePrice);
      setCouponError("");
    }
  };

  if (loadingPlans) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#0859B8] border-t-transparent"></div>
          <span className="font-semibold text-[#222222]">Loading Checkout...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/profile?tab=subscription')}
            className="cursor-pointer w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-900 hover:bg-gray-200 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            Secure Checkout
          </h1>
        </div>
        <img src={logo} alt="4Sight Logo" className="h-10" />
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Card 1: Select Plan */}
          <div className="lg:col-span-3">
            <h2 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</span>
              Select Plan
            </h2>
            <div className="bg-transparent flex flex-col">
              {plans.map((plan) => {
                const isSelected = selectedPlan?.id === plan.id;
                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan)}
                    className={`
                      text-left px-5 py-5 border-b border-gray-200 last:border-b-0 transition-all
                      ${isSelected ? 'bg-blue-50/50 border-l-4 border-l-blue-600 pl-4' : 'hover:bg-gray-50 border-l-4 border-l-transparent pl-4'}
                    `}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-bold text-base ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                        {plan.plan_name}
                      </span>
                      <span className="font-black text-gray-900 text-base">₹{Number(plan.price).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-500">{plan.duration_days} Days Access</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card 2: Plan Type */}
          <div className="lg:col-span-3">
            <h2 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#E7EB90] text-gray-900 flex items-center justify-center text-xs">2</span>
              Plan Type
            </h2>
            <div className={`bg-transparent flex flex-col space-y-3 pr-2 custom-scrollbar ${selectedPlan && Number(selectedPlan.price) === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
              {planTypes.map((type) => {
                const isSelected = selectedPlanType?.id === type.id;
                const bonusText = type.duration_months === 3 ? "+100 bonus credits" : type.duration_months === 12 ? "+500 bonus credits" : null;
                
                return (
                  <div 
                    key={type.id} 
                    onClick={() => setSelectedPlanType(type)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-black text-sm text-gray-900 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded border border-gray-200 border-dashed">{type.name}</span>
                      {isSelected && (
                        <span className="text-xs font-bold text-blue-600 flex items-center gap-1 uppercase tracking-widest">
                          <i className="ri-check-line text-base"></i> Selected
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 font-medium mb-1">
                      Billed for {type.duration_months} month{type.duration_months > 1 ? 's' : ''}
                    </p>
                    {bonusText && (
                      <div className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 mt-1">
                        <i className="ri-gift-line text-sm"></i>
                        {bonusText}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card 3: Apply Offers */}
          <div className="lg:col-span-3">
            <h2 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-gray-300 text-gray-900 flex items-center justify-center text-xs">3</span>
              Apply Offers
            </h2>
            <div className={`bg-transparent flex flex-col ${selectedPlan && Number(selectedPlan.price) === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
              
              <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar">
                {loadingCoupons ? (
                  <p className="text-sm text-gray-500 font-medium">Loading offers...</p>
                ) : availableCoupons.length === 0 ? (
                  <p className="text-sm text-gray-500 font-medium">No offers available at the moment.</p>
                ) : (
                  availableCoupons.map((coupon) => {
                    const cVal = coupon.discount_value;
                    const isDisabled = coupon.discount_type === "FIXED" && selectedPlan && cVal > basePrice;
                    const isApplied = couponApplied && couponCode === coupon.code;
                    
                    return (
                      <div 
                        key={coupon.id} 
                        onClick={() => { if (!isDisabled) isApplied ? handleSkipCoupon() : applyCouponCode(coupon.code); }}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${isApplied ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200 bg-white hover:border-gray-300'} ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-black text-sm text-gray-900 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded border border-gray-200 border-dashed">{coupon.code}</span>
                          {isApplied && (
                            <span className="text-xs font-bold text-blue-600 flex items-center gap-1 uppercase tracking-widest">
                              <i className="ri-check-line text-base"></i> Applied
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 font-medium">
                          {coupon.discount_type === 'PERCENT' ? `Get ${coupon.discount_value}% off` : `Flat ₹${coupon.discount_value} off`} on the total plan amount.
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Card 4: Order Summary */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">4</span>
              Order Summary
            </h2>
            <div className="bg-transparent flex flex-col">
              
              <div className="p-6 border-b border-gray-200 bg-gray-50/50 rounded-t-xl">
                <h3 className="text-xl font-black text-gray-900 mb-1">{selectedPlan?.plan_name || "Select a Plan"}</h3>
                <p className="text-sm font-bold text-gray-500 flex items-center gap-1.5 uppercase tracking-widest">
                  <i className="ri-calendar-event-line"></i> {selectedPlanType ? selectedPlanType.name : "30 Days"} Access
                </p>
              </div>

              <div className="p-6 border-b border-gray-200 space-y-4">
                <div className="flex justify-between text-sm text-gray-900 font-semibold">
                  <span>Base price (x{selectedPlanType?.duration_months || 1} months)</span>
                  <span className="font-black">₹{basePrice.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-900 font-semibold">
                  <span>Convenience fees</span>
                  <span className="text-gray-500">Included</span>
                </div>
                <div className="flex justify-between text-sm text-gray-900 font-semibold">
                  <span>Bonus credits</span>
                  <span className="font-bold text-emerald-600">
                    {selectedPlanType?.duration_months === 3 ? "+100 credits" : selectedPlanType?.duration_months === 12 ? "+500 credits" : "None"}
                  </span>
                </div>
                {couponApplied && (
                  <div className="flex justify-between text-sm text-blue-600 font-bold">
                    <span className="flex items-center gap-1"><i className="ri-discount-percent-fill"></i> Offer ({couponCode})</span>
                    <span>- ₹{discountAmount.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="pt-5 border-t-2 border-gray-200 border-dashed flex justify-between items-center">
                  <span>Order total</span>
                  <span className="font-black text-blue-600 text-2xl">₹{finalPrice.toLocaleString("en-IN")}</span>
                </div>
              </div>

              <div className="p-6 bg-transparent flex flex-col gap-5 rounded-b-xl">
                <p className="text-xs text-gray-500 leading-relaxed font-medium">
                  By proceeding, I express my consent to complete this transaction securely via PayU.
                </p>
                {selectedPlan && (
                  <PaymentButton
                    userId={userId}
                    planId={selectedPlan.id}
                    planTypeId={selectedPlanType?.id}
                    couponCode={couponApplied ? couponCode : undefined}
                    label={`Pay ₹${finalPrice.toLocaleString("en-IN")}`}
                  />
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
